/**
 * use-ad-plans — 投放计划 API（ADM-AP-S001 / S002 / S003）
 *
 * 契约：dev-docs `02-API契约/AF-投放计划.md`。全局模块，**不传 app_id**（后端显式拒 query `app_id`）。
 *
 * `novel_id` / `media_source` 创建后不可改：`UpdateAdPlanRequest` 的 Zod `.strict()` 在后端挡，
 * 前端编辑面也不给这两项的入口 —— 两侧都拦，改哪一侧都不至于静默放行。
 */

import type {
  AdPlanDetail,
  AdPlanListItem,
  AdPlanStatus,
  AdPlanWebLink,
  CreateAdPlanRequest,
  CreateAdPlanWebLinkRequest,
  ReplaceAdPlanLandingPagesRequest,
  UpdateAdPlanRequest,
  UpdateAdPlanWebLinkRequest,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';

/** 列表过滤（AF §2）：三项均可选、AND 关系；空值不进 query string */
export type AdPlanListFilters = {
  novel_id?: string;
  media_source?: string;
  status?: AdPlanStatus;
};

export const adPlansApi = {
  webLinks: (id: string): Promise<{ items: AdPlanWebLink[] }> =>
    apiRequest({ method: 'GET', path: `/api/v1/ad-plans/${id}/web-links` }),
  createWebLink: (id: string, body: CreateAdPlanWebLinkRequest): Promise<AdPlanWebLink> =>
    apiRequest({ method: 'POST', path: `/api/v1/ad-plans/${id}/web-links`, body }),
  updateWebLink: (
    id: string,
    linkId: string,
    body: UpdateAdPlanWebLinkRequest,
  ): Promise<AdPlanWebLink> =>
    apiRequest({ method: 'PATCH', path: `/api/v1/ad-plans/${id}/web-links/${linkId}`, body }),
  removeWebLink: (id: string, linkId: string): Promise<{ ok: boolean }> =>
    apiRequest({ method: 'DELETE', path: `/api/v1/ad-plans/${id}/web-links/${linkId}` }),
  list: (
    page: number,
    limit: number,
    filters: AdPlanListFilters = {},
  ): Promise<OffsetPagedResult<AdPlanListItem>> =>
    apiRequestOffsetPaged<AdPlanListItem>({
      method: 'GET',
      path: '/api/v1/ad-plans',
      // 三个过滤走**服务端**：客户端只筛当前页会让「筛选 + 分页」在多页时给出漏行的结果
      query: { page, limit, ...filters },
    }),
  get: (id: string): Promise<AdPlanDetail> =>
    apiRequest<AdPlanDetail>({ method: 'GET', path: `/api/v1/ad-plans/${id}` }),
  create: (body: CreateAdPlanRequest): Promise<AdPlanDetail> =>
    apiRequest<AdPlanDetail>({ method: 'POST', path: '/api/v1/ad-plans', body }),
  update: (id: string, body: UpdateAdPlanRequest): Promise<AdPlanDetail> =>
    apiRequest<AdPlanDetail>({ method: 'PATCH', path: `/api/v1/ad-plans/${id}`, body }),
  updateStatus: (id: string, status: AdPlanStatus): Promise<AdPlanDetail> =>
    apiRequest<AdPlanDetail>({
      method: 'POST',
      path: `/api/v1/ad-plans/${id}/status`,
      body: { status },
    }),
  replaceLandingPages: (
    id: string,
    body: ReplaceAdPlanLandingPagesRequest,
  ): Promise<AdPlanDetail> =>
    apiRequest<AdPlanDetail>({
      method: 'PUT',
      path: `/api/v1/ad-plans/${id}/landing-pages`,
      body,
    }),
  remove: (id: string): Promise<{ ok: boolean }> =>
    apiRequest<{ ok: boolean }>({ method: 'DELETE', path: `/api/v1/ad-plans/${id}` }),
};
